// src/oura/components/newscan/PlanDecisions.tsx
//
// "For the plan" on a First AI check's Review (JOURNEY-SPEC v1.1 §2.5 item 3):
// the plan's loose ends, closed in the same review so the schedule publishes
// the first time. After the question decisions, under the small label "For
// the plan", at most two cards, both `decide` (Approve waits for them):
//
//   Priority keywords for the plan   the picks as chips; "Use these 8", or
//                                    "Edit them": the chips become a tag
//                                    field (5 to 10, no case duplicates,
//                                    never the business's own name)
//   Outlet names for the plan        one row per outlet, the name only
//                                    (" · address" when one exists); "Use
//                                    these", or "Edit them" (a tag field)
//
// An answered card stays, quieter, with what goes to the plan and "Edit
// them", so the list can change until Approve. Start writes both to the plan.
// No signature card: Oura picks it from the measured first check.

import { CheckIcon } from "lucide-react";
import { useId, useState } from "react";
import type { ScanDraft, ScanDraftDecision } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { SectionHead } from "./SectionHead";
import { PillButton, SURFACE, TYPE } from "@oura/ui";
import { FRAME, plural } from "@oura/newscan/copy";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { DecisionCard } from "./DecisionCard";
import { TagInput } from "./TagInput";
import { Muted } from "./Muted";
import { isPlanCard } from "./firstCheckFacts";

const KEYWORDS_MIN = 5;
const KEYWORDS_MAX = 10;
/** Outlet rows shown before "Show all": a chain lists 24. */
const OUTLETS_SHOWN = 9;

const COPY = {
    label: "For the plan",
    sub: "Saved to the plan when you start. Answer them before you approve the 50.",
    takeN: (n: number) => `Use these ${n}`,
    useNames: "Use these",
    edit: "Edit them",
    cancel: "Cancel",
    keywordsField: "Priority keywords",
    keywordsHint: (n: number) => `${plural(n, "keyword")}. The plan needs ${KEYWORDS_MIN} to ${KEYWORDS_MAX}.`,
    keywordsPlaceholder: "Add a keyword, press Enter",
    tooFew: `Add at least ${KEYWORDS_MIN}.`,
    tooMany: `Keep it to ${KEYWORDS_MAX}.`,
    isBrand: (name: string) => `A keyword cannot be ${name} itself.`,
    namesField: "Outlet names",
    namesHint: "One name per outlet.",
    namesPlaceholder: "Add an outlet, press Enter",
    namesFew: "Name at least 2 outlets.",
    goesKeywords: (n: number) => `${plural(n, "priority keyword")} go to the plan at Start.`,
    goesNames: (n: number) => `${plural(n, "outlet name")} go to the plan at Start.`,
    showAll: (n: number) => `Show all ${n}`,
    showFewer: "Show fewer",
    keywordsList: "Priority keywords",
    namesList: "Outlet names",
} as const;

/** The "For the plan" cards of a First AI check draft, or nothing. */
export function PlanDecisions({ draft, readOnly }: { draft: ScanDraft; readOnly: boolean }) {
    const cards = draft.decisions.filter((d) => isPlanCard(d) && d.state !== "dismissed");
    const labelId = useId();
    if (!cards.length) return null;
    return (
        <section aria-labelledby={labelId} className="space-y-2.5">
            <SectionHead id={labelId} title={COPY.label} sub={COPY.sub} />
            <ul role="list" className="list-plain m-0 space-y-3 p-0">
                {cards.map((d) => (
                    <li key={d.id} data-decision={d.id} tabIndex={-1} className="rounded-2xl outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                        <PlanCard decision={d} brand={draft.business.businessName} readOnly={readOnly} />
                    </li>
                ))}
            </ul>
        </section>
    );
}

function PlanCard({ decision, brand, readOnly }: { decision: ScanDraftDecision; brand: string; readOnly: boolean }) {
    const { ctl } = useScanFlow();
    const keywords = decision.type === "plan_keywords";
    const picks = decision.picks ?? [];
    const answered = decision.state !== "open";
    const [editing, setEditing] = useState<string[] | null>(null);
    const [showAll, setShowAll] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState<"use" | "save" | null>(null);
    const fieldId = useId();
    const hintId = `${fieldId}-hint`;
    const errorId = `${fieldId}-error`;

    const problem = (list: string[]): string | null => {
        if (keywords) {
            if (list.length < KEYWORDS_MIN) return COPY.tooFew;
            if (list.length > KEYWORDS_MAX) return COPY.tooMany;
            const name = brand.trim().toLowerCase();
            // The business's own name as a keyword, not any keyword that happens to contain it (Jack, #4287).
            if (name && list.some((k) => k.trim().toLowerCase() === name)) return COPY.isBrand(brand.trim());
            return null;
        }
        return list.length < 2 ? COPY.namesFew : null;
    };

    const busy = ctl.busy !== null;
    const focusCard = () =>
        requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-decision="${CSS.escape(decision.id)}"]`)?.focus({ preventScroll: true }));

    const takePicks = async () => {
        setError(null);
        setPending("use");
        const fail = await ctl.answer(decision.id, "recommended");
        setPending(null);
        if (fail && fail.kind !== "stale") setError(fail.kind === "network" ? FRAME.network : fail.message);
        else if (!fail) focusCard();
    };

    const save = async () => {
        if (!editing) return;
        const why = problem(editing);
        if (why) {
            setError(why);
            return;
        }
        setError(null);
        setPending("save");
        const fail = await ctl.answerPlan(decision.id, editing);
        setPending(null);
        if (fail) {
            if (fail.kind !== "stale") setError(fail.kind === "network" ? FRAME.network : fail.message);
            return;
        }
        setEditing(null);
        focusCard();
    };

    const startEdit = () => {
        setError(null);
        setEditing([...picks]);
        requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
    };

    const actions = readOnly
        ? []
        : editing
          ? [
                {
                    key: "save",
                    label: keywords ? COPY.takeN(editing.length) : COPY.useNames,
                    kind: "primary" as const,
                    busy: pending === "save",
                    disabled: busy && pending !== "save",
                    onClick: () => void save(),
                },
                { key: "cancel", label: COPY.cancel, kind: "text" as const, disabled: pending === "save", onClick: () => (setEditing(null), setError(null), focusCard()) },
            ]
          : answered
            ? [{ key: "edit", label: COPY.edit, kind: "text" as const, disabled: busy, onClick: startEdit }]
            : [
                  {
                      key: "use",
                      label: decision.recommended?.label ?? (keywords ? COPY.takeN(picks.length) : COPY.useNames),
                      kind: "primary" as const,
                      busy: pending === "use",
                      disabled: busy && pending !== "use",
                      onClick: () => void takePicks(),
                  },
                  { key: "edit", label: COPY.edit, kind: "text" as const, disabled: busy, onClick: startEdit },
              ];

    const shownNames = showAll ? picks : picks.slice(0, OUTLETS_SHOWN);

    return (
        <DecisionCard
            severity={answered && !editing ? "info" : "decide"}
            icon={answered && !editing ? CheckIcon : undefined}
            // Answered reads green, as the core service and the branches do (Jovi, 20 Oct).
            tone={answered && !editing ? "green" : undefined}
            title={decision.title}
            detail={answered && !editing ? (keywords ? COPY.goesKeywords(picks.length) : COPY.goesNames(picks.length)) : decision.detail}
            actions={actions}
        >
            {editing ? (
                <div className="space-y-1.5">
                    <label htmlFor={fieldId} className="sr-only">
                        {keywords ? COPY.keywordsField : COPY.namesField}
                    </label>
                    <TagInput
                        id={fieldId}
                        describedBy={error ? `${hintId} ${errorId}` : hintId}
                        values={editing}
                        onChange={(next) => {
                            setEditing(next);
                            setError(null);
                        }}
                        invalid={Boolean(error)}
                        max={keywords ? KEYWORDS_MAX : undefined}
                        placeholder={keywords ? COPY.keywordsPlaceholder : COPY.namesPlaceholder}
                        disabled={pending === "save"}
                    />
                    <Muted as="p" id={hintId} className="tabular-nums">
                        {keywords ? COPY.keywordsHint(editing.length) : COPY.namesHint}
                    </Muted>
                </div>
            ) : keywords ? (
                <ul role="list" aria-label={COPY.keywordsList} className={cn(SURFACE.inner, "list-plain m-0 flex flex-wrap gap-1.5")}>
                    {picks.map((k) => (
                        <li key={k} className={cn(TYPE.tc, "inline-flex h-7 items-center rounded-full bg-white px-2.5 font-medium text-gray-700 ring-1 ring-inset ring-black/[0.06]")}>
                            {k}
                        </li>
                    ))}
                </ul>
            ) : (
                <div className={cn(SURFACE.inner, "py-1.5")}>
                    <ul role="list" aria-label={COPY.namesList} className="list-plain m-0 divide-y divide-gray-200/70 p-0">
                        {shownNames.map((name) => (
                            <li key={name} className={cn(TYPE.tc, "break-words py-1.5 text-gray-700")}>
                                {name}
                            </li>
                        ))}
                    </ul>
                    {picks.length > OUTLETS_SHOWN && (
                        <PillButton variant="text" className="mt-1" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                            {showAll ? COPY.showFewer : COPY.showAll(picks.length)}
                        </PillButton>
                    )}
                </div>
            )}
            {error && (
                <p id={errorId} role="alert" className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
        </DecisionCard>
    );
}
